import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  IconButton,
  Avatar,
  Divider,
  Paper,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Close as CloseIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  AccountCircle as AccountCircleIcon,
  Badge as BadgeIcon,
} from '@mui/icons-material';
import { getCachedAvatar, cacheAvatar } from '../../utils/profileCache';
import { motion } from 'framer-motion';
import UserAvatar from './UserAvatar';
import { supabase } from '../../lib/supabase';
import ProfileImageModal from './ProfileImageModal';

interface UserInfoModalProps {
  open: boolean;
  onClose: () => void;
  user: {
    name?: string;
    email?: string;
    avatar?: string;
  } | null;
  userAlias?: string | null;
  profileImages?: {
    profile_picture_url?: string;
  } | null;
}

const UserInfoModal: React.FC<UserInfoModalProps> = ({
  open,
  onClose,
  user,
  userAlias,
  profileImages,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [avatarUrl, setAvatarUrl] = useState<string | undefined>(undefined);
  const [imageOpen, setImageOpen] = useState(false);

  // Resolve avatar similar to UserDashboard: Backblaze -> Supabase storage -> props
  useEffect(() => {
    const resolve = async () => {
      try {
        let resolved: string | undefined = undefined;
        const userId = (user as any)?.id || (await supabase.auth.getUser()).data.user?.id;

        if (userId) {
          const cached = getCachedAvatar(userId);
          if (cached) {
            setAvatarUrl(cached);
          }
        }

        // Try Backblaze via PHP endpoint (if session token available)
        try {
          const { data: { session } } = await supabase.auth.getSession();
          if (session?.access_token) {
            const payload: any = { signRead: true, maxKeys: 100 };
            const resp = await fetch('/api/b2-list.php', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
              body: JSON.stringify(payload)
            });
            const raw = await resp.text();
            let json: any = null; try { json = raw ? JSON.parse(raw) : null; } catch {}
            if (resp.ok && json?.items) {
              const items = json.items as Array<{ key: string; url?: string }>;
              const mine = userId ? items.filter(it => it.key && it.key.startsWith(`profile-pictures/${userId}/`)) : [];
              if (mine.length > 0) {
                const last = mine[mine.length - 1];
                resolved = last.url;
              }
            }
          }
        } catch {}

        // Fallback: Supabase storage bucket 'profile-pictures'
        if (!resolved && userId) {
          try {
            const list = await supabase.storage.from('profile-pictures').list(userId, { limit: 100, sortBy: { column: 'created_at', order: 'desc' } } as any);
            const files = (list?.data || []).filter((f: any) => !!f?.name);
            if (files.length > 0) {
              const latest = files[0].name;
              const { data: pub } = supabase.storage.from('profile-pictures').getPublicUrl(`${userId}/${latest}`);
              resolved = pub?.publicUrl;
            }
          } catch {}
        }

        // Final fallbacks
        if (!resolved && profileImages?.profile_picture_url) resolved = profileImages.profile_picture_url;
        if (!resolved && (user as any)?.avatar) resolved = (user as any).avatar;

        setAvatarUrl(resolved);
        if (resolved && userId) {
          cacheAvatar(userId, resolved);
        }
      } catch {}
    };
    resolve();
  }, [user, profileImages]);

  const infoItems = [
    {
      icon: <PersonIcon sx={{ color: '#667eea' }} />,
      label: 'Full Name',
      value: user?.name || 'Not provided',
      color: '#667eea',
    },
    {
      icon: <AccountCircleIcon sx={{ color: '#764ba2' }} />,
      label: 'Username Alias',
      value: userAlias ? `@${userAlias}` : 'Not set',
      color: '#764ba2',
    },
    {
      icon: <EmailIcon sx={{ color: '#f093fb' }} />,
      label: 'Email Address',
      value: user?.email || 'Not provided',
      color: '#f093fb',
    },
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        component: motion.div,
        initial: { opacity: 0, scale: 0.8, y: 50 },
        animate: { opacity: 1, scale: 1, y: 0 },
        exit: { opacity: 0, scale: 0.8, y: 50 },
        transition: { duration: 0.3, ease: 'easeOut' },
        sx: {
          borderRadius: isMobile ? 0 : 4,
          background: '#ffffff',
          border: '1px solid rgba(0,0,0,0.06)',
          boxShadow: '0 20px 40px rgba(0, 0, 0, 0.08)',
          overflow: 'hidden',
        },
      }}
    >
      <DialogContent sx={{ p: 0, position: 'relative' }}>
        {/* Header with close button */}
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 3,
          pb: 2,
          background: '#ffffff',
          borderBottom: '1px solid rgba(0,0,0,0.06)',
        }}>
          <Typography variant="h5" sx={{ fontWeight: 800, color: 'text.primary' }}>
            Profile Information
          </Typography>
          <IconButton 
            onClick={onClose} 
            sx={{ 
              color: 'text.secondary',
              '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)' },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Content */}
        <Box sx={{ p: 3 }}>
          {/* Profile Picture Section */}
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            mb: 4,
            p: 3,
            background: '#fafafa',
            borderRadius: 3,
            border: '1px solid rgba(0,0,0,0.06)',
          }}>
            <UserAvatar
              src={avatarUrl || profileImages?.profile_picture_url || (user as any)?.avatar}
              name={user?.name}
              email={user?.email}
              size={80}
              sx={{
                border: '4px solid #ffffff',
                boxShadow: '0 6px 18px rgba(0, 0, 0, 0.12)',
                mb: 2,
                cursor: 'pointer',
              }}
              onClick={() => setImageOpen(true)}
            />
            <Typography variant="h6" sx={{ 
              fontWeight: 'bold', 
              color: 'text.primary',
              textAlign: 'center',
            }}>
              {user?.name || 'User Profile'}
            </Typography>
          </Box>

          {/* Information Items */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {infoItems.map((item, index) => (
              <motion.div
                key={item.label}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
              >
                <Paper elevation={1} sx={{
                  p: 3,
                  borderRadius: 3,
                  background: '#ffffff',
                  border: '1px solid rgba(0,0,0,0.06)',
                  transition: 'all 0.2s ease',
                  '&:hover': { transform: 'translateY(-1px)', boxShadow: '0 8px 24px rgba(0,0,0,0.08)' },
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{
                      p: 1.25,
                      borderRadius: 2,
                      background: 'rgba(0,0,0,0.04)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}>
                      {item.icon}
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" sx={{ 
                        color: 'text.secondary',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        fontSize: '0.75rem',
                        mb: 0.5,
                      }}>
                        {item.label}
                      </Typography>
                      <Typography variant="h6" sx={{ 
                        color: 'text.primary',
                        fontWeight: 'bold',
                        wordBreak: 'break-all',
                        lineHeight: 1.3,
                      }}>
                        {item.value}
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              </motion.div>
            ))}
          </Box>

          {/* Footer */}
          <Box sx={{ 
            mt: 4, 
            p: 2, 
            textAlign: 'center',
            background: '#fafafa',
            borderRadius: 2,
            border: '1px solid rgba(0,0,0,0.06)',
          }}>
            <Typography variant="body2" sx={{ 
              color: 'text.secondary',
              fontStyle: 'italic',
            }}>
              This information is private and only visible to you
            </Typography>
          </Box>
        </Box>
        <ProfileImageModal open={imageOpen} onClose={() => setImageOpen(false)} src={avatarUrl || profileImages?.profile_picture_url || (user as any)?.avatar} alt={user?.name || 'Profile image'} />
      </DialogContent>
    </Dialog>
  );
};

export default UserInfoModal;
